// src/App.js
import './leaflet-setup';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link,
  Redirect,
  useLocation,
  useHistory,
} from 'react-router-dom';

import Clock from './Clock';
import NewEvent from './NewEvent';
import EventDetail from './EventDetail';
import AuthBar from './AuthBar';
import AuthProvider, { useAuth } from './AuthProvider';
import { PrivacyConsentBanner, PrivacyPage, PrivacySettingsModal, TermsPage } from './privacy';
import ContactPage from './ContactPage';
import { savedEventsStore } from './lib/savedEvents';
import HeartButton from './lib/HeartButton';
import { API_BASE } from './api';
import ModerationPanel from './ModerationPanel';
import useTypewriter from './useTypewriter';
import { HOME_BACKGROUND } from './eventBackground';

import './index.css';
import { formatDateTimeRange } from './formatDate';
import { getCityCountry } from './eventPlace';
import NotificationBell from './NotificationBell';
import ProfilePage from './ProfilePage';
import Avatar from './Avatar';
import { flushSync } from 'react-dom';
import PullToRefresh from './PullToRefresh';
import { markNavigated } from './navState';
import { describeParsedEvent, parseEventText } from './parseEventText';
import { saveEventDraft } from './eventDraft';

function prefersReducedMotion() {
  return Boolean(
    typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

// Example searches typed into the empty search box while it has focus.
const SEARCH_PROMPTS = [
  'this sunday picnic in the park at 12:00',
  'A chill night with friends',
  'dancing on monday',
  'friday night plans for two',
];

/* ----------------------------- helpers ------------------------------ */

// Opening an event from a card: the browser cross-fades the feed into the
// event page as it slides up (View Transitions), so there's no blank frame
// and the background change blends in. Browsers without it navigate normally
// and the page's own slide-up plays (.event-arrive in index.css).
function openEventSmoothly(clickEvent, history, path) {
  if (
    clickEvent.defaultPrevented ||
    clickEvent.button !== 0 ||
    clickEvent.metaKey || clickEvent.ctrlKey || clickEvent.shiftKey || clickEvent.altKey ||
    typeof document.startViewTransition !== 'function' ||
    prefersReducedMotion()
  ) {
    return;
  }

  clickEvent.preventDefault();
  const root = document.documentElement;
  root.classList.add('vt-open-event');
  const transition = document.startViewTransition(() => {
    // smoothOpen: the transition already slides the page in, so the event
    // page skips its own slide-up (otherwise it plays a second time).
    flushSync(() => history.push(path, { smoothOpen: true }));
    window.scrollTo(0, 0);
  });
  transition.finished.finally(() => root.classList.remove('vt-open-event'));
}

function userKeyFromUser(user) {
  return user?.email?.toLowerCase() || null;
}
function normaliseEmail(value = '') {
  return String(value).trim().toLowerCase();
}

function isEventOwner(event, userKey) {
  if (!userKey) {
    return false;
  }

  const creatorEmail = normaliseEmail(
    event.creatorEmail ||
      event.createdBy ||
      event.hostEmail
  );

  return creatorEmail === userKey;
}

function eventHeartKey(event) {
  return String(
    event.id ||
      event.slug ||
      event._id ||
      ''
  );
}

function isLikelyCity(text = '') {
  const value = String(text).trim();

  if (!value) {
    return false;
  }

  if (/\d/.test(value)) {
    return false;
  }

  if (!/^[A-Za-zÀ-ÖØ-öø-ÿ' .-]{2,}$/.test(value)) {
    return false;
  }

  const deniedValues = new Set([
    'unknown',
    'n/a',
    'na',
    'none',
    'null',
  ]);

  if (deniedValues.has(value.toLowerCase())) {
    return false;
  }

  return true;
}

function toTitleCase(name = '') {
  return String(name)
    .trim()
    .toLowerCase()
    .replace(
      /\b[^\s-']+/g,
      (word) =>
        word.charAt(0).toUpperCase() +
        word.slice(1)
    );
}

/* ----------------------------- Home page ----------------------------- */

function Home({
  events,
  searchTerm,
  onSearchChange,
  showMine,
  onToggleMine,
  user,
  locFilter,
  setLocFilter,
  onCreateEvent,
  onCreateFromText,
  myEvents,
}) {
  const history = useHistory();

  const [isFocused, setIsFocused] =
    useState(false);

  // A sentence like "this sunday picnic at 12:00" can become a new event:
  // Enter (or the hint under the bar) opens the create page filled in.
  const parsedEvent = useMemo(() => parseEventText(searchTerm), [searchTerm]);
  const createFromSearch = () => {
    if (parsedEvent.isEvent && onCreateFromText) onCreateFromText(parsedEvent);
  };

  const [typingDemo, setTypingDemo] =
    useState(false);
  const typedPrompt = useTypewriter(SEARCH_PROMPTS, typingDemo);

  const [plusSpinning, setPlusSpinning] =
    useState(false);

  // Set while a click is waiting for the spin to finish (holds a fallback
  // timer in case the animation end event never arrives).
  const createAfterSpinRef = useRef(null);
  const openCreateAfterSpin = () => {
    window.clearTimeout(createAfterSpinRef.current);
    createAfterSpinRef.current = null;
    onCreateEvent();
  };

  const key = userKeyFromUser(user);

  const canSee = useCallback(
    (event) =>
      event.isPublic === true ||
      event.public === true ||
      isEventOwner(event, key),
    [key]
  );

  const [heartTick, setHeartTick] = useState(0);
  const [savedEventIds, setSavedEventIds] = useState(new Set());

  useEffect(() => {
    let isCancelled = false;

    async function refreshSavedEvents() {
      const items = await savedEventsStore.list();
      if (isCancelled) {
        return;
      }

      setSavedEventIds(new Set(items.map((item) => String(item.id))));
    }

    refreshSavedEvents();

    return () => {
      isCancelled = true;
    };
  }, [heartTick, key]);

  const liveHeartSet = savedEventIds;

  const { cities, countries } =
    useMemo(() => {
      const citySet = new Set();
      const countrySet = new Set();

      for (const event of events) {
        if (!canSee(event)) {
          continue;
        }

        const { city, country } =
          getCityCountry(event);

        if (country) {
          countrySet.add(
            toTitleCase(country)
          );
        }

        if (
          city &&
          isLikelyCity(city)
        ) {
          citySet.add(
            toTitleCase(city)
          );
        }
      }

      if (
        locFilter?.type === 'city' &&
        locFilter.value &&
        isLikelyCity(locFilter.value)
      ) {
        citySet.add(
          toTitleCase(locFilter.value)
        );
      }

      if (
        locFilter?.type === 'country' &&
        locFilter.value
      ) {
        countrySet.add(
          toTitleCase(locFilter.value)
        );
      }

      return {
        cities: Array.from(citySet).sort(
          (a, b) => a.localeCompare(b)
        ),

        countries: Array.from(
          countrySet
        ).sort((a, b) =>
          a.localeCompare(b)
        ),
      };
    }, [
      events,
      canSee,
      locFilter,
    ]);

  const filtered = useMemo(() => {
    const heartedEventIds = new Set(
      events
        .filter((event) => {
          const heartKey = eventHeartKey(event);
          return (
            key &&
            heartKey &&
            liveHeartSet.has(heartKey)
          );
        })
        .map((event) =>
          String(event.id || event.slug || event._id || '')
        )
        .filter(Boolean)
    );

    const combinedEvents = showMine
      ? [
          ...myEvents,
          ...events.filter((event) =>
            heartedEventIds.has(
              String(event.id || event.slug || event._id || '')
            )
          ),
        ]
      : events;

    const uniqueEvents = new Map();

    combinedEvents.forEach((event) => {
      const eventId = String(event.id || event.slug || event._id || '');
      if (!eventId || uniqueEvents.has(eventId)) {
        return;
      }

      uniqueEvents.set(eventId, event);
    });

    return Array.from(uniqueEvents.values())
      .filter((event) => {
        const title = String(event.title || '');

        if (
          !title
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        ) {
          return false;
        }

        if (locFilter?.type) {
          const { city, country } = getCityCountry(event);

          const normaliseLocationPart = (value = '') =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/^\d{4,6}\s+/, '')
    .replace(/\s+/g, ' ');

if (
  locFilter.type === 'city' &&
  normaliseLocationPart(city) !==
    normaliseLocationPart(locFilter.value)
) {
  return false;
}

if (
  locFilter.type === 'country' &&
  normaliseLocationPart(country) !==
    normaliseLocationPart(locFilter.value)
) {
  return false;
}
        }

        return true;
      })
      .sort((a, b) => {
        // Featured events (the example event) stay pinned at the top.
        if (Boolean(a.featured) !== Boolean(b.featured)) {
          return a.featured ? -1 : 1;
        }
        const aTime = new Date(a.createdAt).getTime();
        const bTime = new Date(b.createdAt).getTime();
        return bTime - aTime;
      });
  }, [
    events,
    myEvents,
    showMine,
    key,
    liveHeartSet,
    searchTerm,
    locFilter,
  ]);

  const [openLoc, setOpenLoc] =
    useState(false);

  const MAPBOX_TOKEN =
    process.env.REACT_APP_MAPBOX_TOKEN;

  const toggleLoc = () => {
    setOpenLoc((value) => !value);
  };

  const useMyLocation = async () => {
    if (!navigator.geolocation) {
      alert(
        'Geolocation not supported.'
      );

      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // Round to 3 decimals (~100 m) so the map provider never gets an exact
          // position; this is what users agree to at sign-up.
          const latitude = Number(position.coords.latitude.toFixed(3));
          const longitude = Number(position.coords.longitude.toFixed(3));

          if (!MAPBOX_TOKEN) {
            alert(
              'Missing REACT_APP_MAPBOX_TOKEN in .env'
            );

            return;
          }

          const url =
            `https://api.mapbox.com/geocoding/v5/mapbox.places/` +
            `${longitude},${latitude}.json?` +
            `types=place,country&access_token=${MAPBOX_TOKEN}`;

          const response =
            await fetch(url);

          const data =
            await response.json();

          const place = (
            data.features || []
          ).find((feature) =>
            (
              feature.place_type || []
            ).includes('place')
          );

          const country = (
            data.features || []
          ).find((feature) =>
            (
              feature.place_type || []
            ).includes('country')
          );

          const cityName =
            place?.text || '';

          const countryName =
            country?.text || '';

          if (cityName) {
            setLocFilter({
              type: 'city',
              value:
                toTitleCase(cityName),
            });
          } else if (countryName) {
            setLocFilter({
              type: 'country',
              value:
                toTitleCase(
                  countryName
                ),
            });
          } else {
            alert(
              'Could not determine your city or country from location.'
            );
          }

          setOpenLoc(false);
        } catch (error) {
          console.error(
            'Reverse geocoding failed:',
            error
          );

          alert(
            'Reverse geocoding failed.'
          );
        }
      },

      () => {
        alert(
          'Permission denied or location unavailable.'
        );
      },

      {
        enableHighAccuracy: true,
        timeout: 8000,
      }
    );
  };

  const isChosenPlace = (type, value) =>
    locFilter?.type === type && locFilter?.value === value;

  // Picks a city/country filter; picking the chosen one again clears it.
  const choosePlace = (type, value) => {
    setLocFilter(isChosenPlace(type, value) ? null : { type, value });
    setOpenLoc(false);
  };

  const clearLoc = () => {
    setLocFilter(null);
    setOpenLoc(false);
  };

  return (
    <div style={homeStyles.container}>
      <div style={homeStyles.controls}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            className={plusSpinning ? 'new-event-button is-spinning' : 'new-event-button'}
            style={homeStyles.newButton}
            onClick={() => {
              // Spin on click too, then open the create page once it lands.
              if (prefersReducedMotion()) {
                onCreateEvent();
                return;
              }
              if (createAfterSpinRef.current) return;
              createAfterSpinRef.current = window.setTimeout(openCreateAfterSpin, 1000);
              setPlusSpinning(true);
            }}
            onMouseEnter={() => setPlusSpinning(true)}
            onAnimationEnd={() => {
              setPlusSpinning(false);
              if (createAfterSpinRef.current) openCreateAfterSpin();
            }}
            aria-label="Create a new event"
          >
            <img
              src="/frolik-logo.png"
              alt=""
              className="new-event-frog"
            />
          </button>

          <div className="search-field">
            <input
              type="text"
              className={typingDemo ? 'search-input is-demo' : 'search-input'}
              placeholder={typingDemo ? '' : 'Events for All'}
              aria-label="Search events"
              value={searchTerm}
              onChange={(event) => {
                setTypingDemo(false);
                onSearchChange(event);
              }}
              onClick={() => {
                if (!searchTerm && !prefersReducedMotion()) setTypingDemo(true);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && parsedEvent.isEvent) {
                  event.preventDefault();
                  createFromSearch();
                }
              }}
              enterKeyHint={parsedEvent.isEvent ? 'go' : 'search'}
              style={{
                ...homeStyles.searchInput,

                ...(isFocused
                  ? homeStyles.searchInputFocus
                  : {}),
              }}
              onFocus={() => {
                setIsFocused(true);
                if (!searchTerm && !prefersReducedMotion()) setTypingDemo(true);
              }}
              onBlur={() => {
                setIsFocused(false);
                setTypingDemo(false);
              }}
            />

            {typingDemo && (
              <span className="search-typing" aria-hidden="true">
                {typedPrompt}
                <span className="search-typing-caret" />
              </span>
            )}
          </div>

          {parsedEvent.isEvent && (
            <button
              type="button"
              className="search-create-hint"
              // Keep focus in the search bar so the hint doesn't vanish mid-tap.
              onMouseDown={(event) => event.preventDefault()}
              onClick={createFromSearch}
            >
              <span className="search-create-pill">
                <span className="search-create-label">Press Enter to create</span>
                <strong>{parsedEvent.title}</strong>
                <span className="search-create-details">{describeParsedEvent(parsedEvent)}</span>
              </span>
            </button>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1rem',
            marginTop: '0.8rem',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            className="feed-toggle"
            onClick={onToggleMine}
            style={{
              ...homeStyles.myBtn,

              ...(showMine
                ? homeStyles.myBtnActive
                : {}),

              ...(key
                ? {}
                : {
                    opacity: 0.6,
                    cursor:
                      'not-allowed',
                  }),
            }}
            disabled={!key}
          >
            {/* Names the feed being shown; tapping switches to the other. */}
            {showMine ? "My Events" : "Public"}
          </button>

          <div
            style={{
              position: 'relative',
            }}
          >
            {/* White on orange while a city/country filter is on. */}
            <button
              type="button"
              className="feed-toggle"
              onClick={toggleLoc}
              aria-pressed={Boolean(locFilter?.value)}
              style={{
                ...homeStyles.locationBtn,
                ...(locFilter?.value
                  ? homeStyles.myBtnActive
                  : {}),
              }}
            >
              {locFilter?.value || 'Locations'} ▾
            </button>

            {openLoc && (
              <div
                style={
                  homeStyles.locationMenu
                }
              >
                <div
                  style={
                    homeStyles.menuHeader
                  }
                >
                  Location
                </div>

                <button
                  type="button"
                  style={
                    homeStyles.menuItem
                  }
                  onClick={useMyLocation}
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 32 32"
                    width="18"
                    height="18"
                    style={{
                      display: 'inline-block',
                      verticalAlign: 'middle',
                      marginRight: 8,
                      shapeRendering: 'crispEdges',
                    }}
                  >
                    <path
                      fill="currentColor"
                      d="M10 4h12v4h-3v6h4v2h2v4h-7v8h-4v-8H7v-4h2v-2h4V8h-3z"
                    />
                  </svg>
                  Use my location
                </button>

                {locFilter?.type && (
                  <button
                    type="button"
                    style={{
                      ...homeStyles.menuItem,
                      color: '#c00',
                    }}
                    onClick={clearLoc}
                  >
                    Clear location filter
                  </button>
                )}

                <div
                  style={
                    homeStyles.menuSectionTitle
                  }
                >
                  Cities
                </div>

                <div
                  style={
                    homeStyles.menuList
                  }
                >
                  {cities.length ? (
                    cities.map((city) => (
                      <button
                        type="button"
                        key={`city-${city}`}
                        aria-pressed={isChosenPlace('city', city)}
                        style={{
                          ...homeStyles.choice,
                          ...(isChosenPlace('city', city)
                            ? homeStyles.choiceActive
                            : {}),
                        }}
                        onClick={() => {
                          choosePlace('city', city);
                        }}
                      >
                        {city}
                      </button>
                    ))
                  ) : (
                    <div
                      style={
                        homeStyles.menuEmpty
                      }
                    >
                      No cities yet
                    </div>
                  )}
                </div>

                <div
                  style={
                    homeStyles.menuSectionTitle
                  }
                >
                  Countries
                </div>

                <div
                  style={
                    homeStyles.menuList
                  }
                >
                  {countries.length ? (
                    countries.map(
                      (country) => (
                        <button
                          type="button"
                          key={`country-${country}`}
                          aria-pressed={isChosenPlace('country', country)}
                          style={{
                            ...homeStyles.choice,
                            ...(isChosenPlace('country', country)
                              ? homeStyles.choiceActive
                              : {}),
                          }}
                          onClick={() => {
                            choosePlace('country', country);
                          }}
                        >
                          {country}
                        </button>
                      )
                    )
                  ) : (
                    <div
                      style={
                        homeStyles.menuEmpty
                      }
                    >
                      No countries yet
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={homeStyles.grid}>
        {filtered.length > 0 ? (
          filtered.map((event) => {
            const {
              city,
              country,
            } = getCityCountry(event);

            const date = new Date(
              event.dateTime
            );

            return (
              <Link
                key={
                  event.id ||
                  event.slug
                }
                to={`/event/${
                  event.slug ||
                  event.id
                }`}
                onClick={(clickEvent) =>
                  openEventSmoothly(clickEvent, history, `/event/${event.slug || event.id}`)
                }
                style={
                  homeStyles.cardLink
                }
              >
                <div
                  className="event-card"
                  style={
                    homeStyles.card
                  }
                >

{event.isPublic === false && (
  <div
    style={{
      position: "absolute",
      top: 12,
      left: 12,
      width: 12,
      height: 12,
      borderRadius: "50%",
      backgroundColor: "#DC2626",
      zIndex: 6,
    }}
  />
)}

                  <HeartButton
                    event={event}
                    disabled={!key}
                    onToggle={() => {
                      setHeartTick((tick) => tick + 1);
                    }}
                    style={homeStyles.heartBtn}
                  />

                  {event.banner ? (
                    <img
                      src={event.banner}
                      alt="Event banner"
                      style={
                        homeStyles.cardBanner
                      }
                    />
                  ) : (
                    <div
                      style={
                        homeStyles.cardBannerPlaceholder
                      }
                    >
                      <img
                        src="/frolik-logo.png"
                        alt=""
                        style={homeStyles.cardBannerLogo}
                      />
                    </div>
                  )}

                  <div
                    className="event-card-metal"
                    style={
                      homeStyles.cardBody
                    }
                  >
                    <h3
                      className="event-card-title"
                      style={
                        homeStyles.cardTitle
                      }
                    >
                      {event.title}
                    </h3>

                    {showMine && event.status && event.status !== 'approved' && (
                      <div className="my-event-review-status">
                        {event.status === 'rejected'
                          ? `Not approved: ${event.rejectReason || 'Please review the event details.'}`
                          : 'Waiting for review'}
                      </div>
                    )}

                    <div
                      className="event-card-line"
                      style={
                        homeStyles.cardLine
                      }
                    >
                      {Number.isNaN(
                        date.getTime()
                      )
                        ? ''
                        : formatDateTimeRange(date, event.endDateTime)}
                    </div>

                    <div
                      className="event-card-line"
                      style={
                        homeStyles.cardLine
                      }
                    >
                      {[
                        city,
                        country,
                      ]
                        .filter(Boolean)
                        .join(', ') ||
                        'Unknown location'}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })
        ) : (
          <p>
            {showMine ||
            locFilter?.type
              ? 'No events match your current filters.'
              : 'No public events yet.'}
          </p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------- App + Footer ------------------------------- */

const FOOTER_HEIGHT = 112;

function FooterBar({ onOpenPrivacy }) {
  const { user } = useAuth();

  const greetName =
    user?.username ||
    user?.name ||
    (
      user?.email
        ? user.email.split('@')[0]
        : null
    );

  return (
    <footer
      className="footer-metal site-footer"
      style={styles.footerGlass}
    >
      <div className="footer-status-row" style={styles.footerStatusRow} role="status">
        Saved on this device only
        <span
          aria-label="Active"
          className="footer-status-star"
          style={styles.footerStatusIndicator}
        >
          ✶
        </span>
      </div>

      <div className="footer-bar" style={styles.footerBar}>
        <div
          style={
            styles.footerCellLeft
          }
        >
          {/* Frog: home. Greeting + avatar: your profile (signed in). */}
          <Link to="/" className="footer-home" aria-label="Frolik home">
            <img
              src="/frolik-logo.png"
              alt="Frolik logo"
              style={{
                // Same height as the footer clock text block.
                height: 22,
                width: 'auto',
                imageRendering:
                  'pixelated',
              }}
            />
            {!greetName && (
              <span style={styles.footerLogo}>FROLIK</span>
            )}
          </Link>

          {greetName && (
            <Link to="/profile" className="footer-profile" title="Your profile">
              <span className="footer-greeting" style={styles.footerLogo}>
                <span className="footer-hello">Hello, </span>
                {greetName}
                <span className="footer-hello">!</span>
              </span>
              <Avatar user={user} size={22} />
            </Link>
          )}
        </div>

        <div
          style={
            styles.footerCellCenter
          }
        >
          <AuthBar />
        </div>

        <div
          style={
            styles.footerCellRight
          }
        >
          <NotificationBell />
          <Clock />
        </div>
      </div>

      <nav className="footer-legal" style={styles.footerLegalRow} aria-label="About and legal information">
        <Link to="/about" style={styles.footerLegalLink}>About &amp; Terms</Link>
        <span aria-hidden="true" style={styles.footerLegalSeparator}>✶</span>
        <Link to="/privacy" style={styles.footerLegalLink}>Privacy</Link>
        <span aria-hidden="true" style={styles.footerLegalSeparator}>✶</span>
        <Link to="/contact" style={styles.footerLegalLink}>Contact</Link>
        <span aria-hidden="true" style={styles.footerLegalSeparator}>✶</span>
        <button
          type="button"
          onClick={onOpenPrivacy}
          style={styles.footerPrivacyButton}
        >
          Your data
        </button>
      </nav>
    </footer>
  );
}

/* -------------------------- Protected create route -------------------------- */

function RequireAccount() {
  const { signUp } = useAuth();

  useEffect(() => {
    signUp();

    // We deliberately open the modal
    // once when this route loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Redirect to="/" />;
}

/* -------------------------------- App shell -------------------------------- */

function AppShell() {
  const { user, signIn, signUp, token } =
    useAuth();

  const location = useLocation();
  const history = useHistory();
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const [events, setEvents] =
    useState([]);
  const [myEvents, setMyEvents] =
    useState([]);

  const [
    searchTerm,
    setSearchTerm,
  ] = useState('');

  const [
    showMine,
    setShowMine,
  ] = useState(false);

  const [
    locFilter,
    setLocFilter,
  ] = useState(null);

  const creatorKey =
    userKeyFromUser(user);

  const handleCreateEvent = () => {
    if (!user) {
      signUp();
      return;
    }

    history.push('/new');
  };

  // After the first move within the site, pages play their own slide-up.
  // Checked while rendering (the shell renders before the page), so the page
  // already knows when it first draws.
  const firstLocationRef = useRef(location);
  if (location !== firstLocationRef.current) markNavigated();

  // Everything this person can already see (public feed plus their own and
  // invited events), so an event page opens instantly from any list instead
  // of showing "Loading event..." first.
  const knownEvents = useMemo(() => [...myEvents, ...events], [myEvents, events]);

  // From the search bar: keep what was typed, then open the create page,
  // after signing in if needed.
  const createAfterSignInRef = useRef(false);
  const handleCreateFromText = (parsed) => {
    saveEventDraft({ title: parsed.title, date: parsed.date, time: parsed.time, endTime: parsed.endTime, location: parsed.location });
    setSearchTerm('');
    if (!user) {
      createAfterSignInRef.current = true;
      signIn();
      return;
    }
    history.push('/new');
  };

  useEffect(() => {
    if (user && createAfterSignInRef.current) {
      createAfterSignInRef.current = false;
      history.push('/new');
    }
  }, [user, history]);

  const loadEvents = async () => {
    try {
      const url = `${API_BASE}/api/events`;

      const response = await fetch(
        url
      );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ||
            'Failed to load events'
        );
      }

      setEvents(
        Array.isArray(data)
          ? data
          : []
      );
    } catch (error) {
      console.error(
        'Failed to load events:',
        error
      );
    }
  };

  const loadMyEvents = async () => {
    if (!token) {
      setMyEvents([]);
      return;
    }

    try {
      const url = `${API_BASE}/api/my-events`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ||
            'Failed to load my events'
        );
      }

      setMyEvents(
        Array.isArray(data)
          ? data
          : []
      );
    } catch (error) {
      console.error(
        'Failed to load my events:',
        error
      );
      setMyEvents([]);
    }
  };

useEffect(() => {
  loadEvents();
  loadMyEvents();

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [creatorKey, token]);

  // Refresh when returning to the feed, e.g. after approving events in /admin.
  const isHomePage = location?.pathname === '/';
  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      // The effect above already loads on first render.
      isInitialRenderRef.current = false;
      return;
    }
    if (isHomePage) {
      loadEvents();
      loadMyEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHomePage]);

  const pageBg = useMemo(() => {
    const path =
      location?.pathname || '/';

    const match = path.match(
      /^\/(?:event|app)\/(.+)$/
    );

    if (match) {
      // EventDetail loads the event itself (including private and pending
      // ones that aren't in the public feed) and sets --page-bg from it.
      return 'var(--page-bg)';
    }

    return HOME_BACKGROUND;
  }, [
    location?.pathname,
  ]);

  const addEvent = (eventData) => {
    const normalizedEvent =
      eventData?.event ||
      eventData;

    if (!normalizedEvent) {
      loadEvents();
      return;
    }

    setEvents(
      (previousEvents) => [
        normalizedEvent,
        ...previousEvents,
      ]
    );
  };

  const deleteEvent = (id) => {
      setEvents(
        (previousEvents) =>
          previousEvents.filter(
            (event) =>
              event.id !== id &&
              event.slug !== id
          )
      );
  };

  return (
    <div
      style={{
        ...styles.page,
        background: pageBg,
      }}
    >
      <PrivacyConsentBanner />
      <PrivacySettingsModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
      <PullToRefresh onRefresh={() => Promise.all([loadEvents(), loadMyEvents()])} />

      <main className="app-main" style={styles.main}>
        <Switch>
          <Route exact path="/admin">
            <ModerationPanel token={token} />
          </Route>

          <Route exact path="/">
            <Home
              events={events}
              searchTerm={
                searchTerm
              }
              onSearchChange={(
                event
              ) =>
                setSearchTerm(
                  event.target.value
                )
              }
              showMine={showMine}
              onToggleMine={() =>
                setShowMine(
                  (value) =>
                    !value
                )
              }
              user={user}
              locFilter={locFilter}
              setLocFilter={
                setLocFilter
              }
              onCreateEvent={
                handleCreateEvent
              }
              onCreateFromText={handleCreateFromText}
              myEvents={myEvents}
            />
          </Route>

          <Route path="/privacy">
            <PrivacyPage />
          </Route>

          <Route path={['/about', '/terms']}>
            <TermsPage />
          </Route>

          <Route path="/contact">
            <ContactPage />
          </Route>

          <Route path="/profile">
            {user ? <ProfilePage /> : <RequireAccount />}
          </Route>

          <Route path="/new">
            {user ? (
              <NewEvent
                addEvent={
                  addEvent
                }
              />
            ) : (
              <RequireAccount />
            )}
          </Route>

          <Route path="/event/:id">
            <EventDetail
              events={knownEvents}
              deleteEvent={
                deleteEvent
              }
            />
          </Route>

          <Route path="/app/:id">
            <EventDetail
              events={knownEvents}
              deleteEvent={
                deleteEvent
              }
            />
          </Route>
        </Switch>
      </main>

      <FooterBar onOpenPrivacy={() => setPrivacyOpen(true)} />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <AppShell />
      </Router>
    </AuthProvider>
  );
}

/* ------------------------------- Styles ------------------------------ */

const styles = {
  page: {
    position: 'relative',
    minHeight: '100vh',
    background: HOME_BACKGROUND,
  },

  main: {
    paddingTop: 12,

    paddingBottom:
      `calc(${FOOTER_HEIGHT}px + env(safe-area-inset-bottom) + 24px)`,
  },

  footerGlass: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    minHeight: FOOTER_HEIGHT,
    boxSizing: 'border-box',
    padding: '4px 0 calc(8px + env(safe-area-inset-bottom))',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 2,

    // Background, edges and shadow: .footer-metal in index.css (brushed metal).

    zIndex: 20,
  },

  footerStatusRow: {
    maxWidth: 1100,
    width: '100%',
    margin: '0 auto',
    minHeight: 18,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#111827',
    fontFamily: "'Reddit Mono', monospace",
    fontSize: '10px',
    lineHeight: 1.2,
    gap: 6,
  },

  footerStatusIndicator: {
    display: 'inline-block',
    flex: '0 0 auto',
    color: '#e53935',
    textShadow: '0 0 4px rgba(229, 57, 53, 0.55)',
    animation: 'footer-status-pulse 1.2s ease-in-out infinite',
  },

  footerBar: {
    maxWidth: 1100,
    margin: '0 auto',
    minHeight: 40,
    width: '100%',
    padding: '2px 16px',
    display: 'grid',

    gridTemplateColumns:
      '1fr auto 1fr',

    alignItems: 'center',

    fontFamily:
      "'Reddit Mono', monospace",

    boxSizing: 'border-box',
  },

  footerCellLeft: {
    justifySelf: 'start',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },

  footerCellCenter: {
    justifySelf: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },

  footerCellRight: {
    justifySelf: 'end',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
  },

  footerLogo: {
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: 1,
  },

  footerLegalRow: {
    minHeight: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    fontFamily: "'Reddit Mono', monospace",
    lineHeight: 1,
  },

  footerLegalLink: {
    color: '#111827',
    fontSize: '9px',
    textDecoration: 'underline',
    textUnderlineOffset: 2,
  },

  footerLegalSeparator: {
    color: 'rgba(17, 24, 39, 0.65)',
    fontSize: '10px',
  },

  footerPrivacyButton: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    color: '#111827',
    padding: 0,
    cursor: 'pointer',
    fontFamily: "'Reddit Mono', monospace",
    fontSize: '9px',
    lineHeight: 1,
    textDecoration: 'underline',
    textUnderlineOffset: 2,
  },
};

/* --------------------------- Home Styles ----------------------------- */

const homeStyles = {
  container: {
    textAlign: 'center',
    padding: '2rem',
  },

  controls: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.4rem',
    marginBottom: '2rem',
  },

  newButton: {
    width: 44,
    height: 44,
    borderRadius: '50%',

    border:
      '1px solid rgba(255,255,255,0.5)',

    // Faint circle so the white "+" stands out (matches the search box).
    background:
      'rgba(255,255,255,0.22)',

    color: '#fff',

    backdropFilter:
      'blur(10px)',

    fontFamily:
      "'Reddit Mono', monospace",

    cursor: 'pointer',
  },

  searchInput: {
    padding: '0.8rem 1.4rem',
    borderRadius: '10px',

    border:
      '1px solid rgba(255, 255, 255, 0.35)',

    background:
      'rgba(255, 255, 255, 0.22)',

    backdropFilter:
      'blur(12px)',

    WebkitBackdropFilter:
      'blur(12px)',

    fontSize: '1rem',
    minWidth: '260px',
    color: '#fff',

    fontFamily:
      "'Reddit Mono', monospace",

    outline: 'none',

    transition:
      'all 0.3s ease',
  },

  // Focused: a slightly brighter edge, no glow.
  searchInputFocus: {
    border:
      '1px solid rgba(255, 255, 255, 0.6)',
  },

  myBtn: {
  padding: '0.6rem 1.2rem',
  minWidth: 110,
  justifyContent: 'center',
  borderRadius: '10px',
  border: '1px solid #c9c9c9',
  background: 'linear-gradient(180deg, #fff 0%, #f3f3f3 100%)',
  color: '#111',
  fontSize: '0.7rem',
  fontFamily: "'Reddit Mono', monospace",
  cursor: 'pointer',
  boxShadow: '0 1px 2px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.9)',
  transition: 'background 0.15s ease, box-shadow 0.15s ease',
},

  // "My Events" switched on (button reads "Public"): white on orange, no
  // visible border. The border stays as a transparent 1px so the size doesn't jump.
  myBtnActive: {
    background: 'linear-gradient(180deg, #fdc18f 0%, #ff7e14 48%, #e66700 100%)',
    border: '1px solid transparent',
    color: '#fff',
    boxShadow: '0 2px 10px rgba(230, 103, 0, 0.45), inset 0 1px 0 rgba(255,255,255,0.35)',
  },

  locationBtn: {
    padding: '0.6rem 1.2rem',
    borderRadius: '10px',
    border: '1px solid #c9c9c9',
    background: 'linear-gradient(180deg, #fff 0%, #f3f3f3 100%)',
    color: '#111',
    fontSize: '0.7rem',

    fontFamily:
      "'Reddit Mono', monospace",

    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.9)',
    transition: 'background 0.15s ease, box-shadow 0.15s ease',
  },

  locationMenu: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    width: 280,
    maxHeight: 360,
    overflowY: 'auto',
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 6,

    boxShadow:
      '0 8px 24px rgba(0,0,0,.15)',

    padding: 10,
    zIndex: 30,
    textAlign: 'left',
  },

  menuHeader: {
    fontWeight: 700,
    fontSize: 13,
    marginBottom: 6,
  },

  menuSectionTitle: {
    marginTop: 10,
    fontSize: 12,
    fontWeight: 700,
  },

  menuList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 6,
  },

  menuItem: {
    width: '100%',
    textAlign: 'left',
    padding: '8px 10px',
    borderRadius: 4,
    border: '1px solid #eee',
    background: '#fff',
    cursor: 'pointer',
  },

  choice: {
    padding: '6px 10px',
    borderRadius: '10px',
    border: '1px solid #ddd',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 12,
  },

  // Chosen city/country: white on orange, like the active My Events button.
  choiceActive: {
    background: 'linear-gradient(180deg, #fdc18f 0%, #ff7e14 48%, #e66700 100%)',
    border: '1px solid transparent',
    color: '#fff',
  },

  menuEmpty: {
    fontSize: 12,
    color: '#777',
    padding: '6px 0',
  },

  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2rem',
    justifyContent: 'center',
  },

  cardLink: {
    textDecoration: 'none',
  },

  card: {
    width: '240px',
    height: '220px',
    borderRadius: '0.5rem',
    overflow: 'hidden',

    background:
      'rgba(255,255,255,0.2)',

    backdropFilter:
      'blur(12px)',

    boxShadow:
      '0 4px 24px rgba(0,0,0,0.1)',

    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },

  heartBtn: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 30,
    height: 30,
    borderRadius: '50%',
    border: '1px solid #000',
    background: '#fff',
    color: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    cursor: 'pointer',
    zIndex: 5,
    padding: 0,
    lineHeight: 1,
  },

  heartBtnActive: {
    background: '#000',
    color: '#fff',
  },

  cardBanner: {
    width: '100%',
    height: '140px',
    objectFit: 'cover',
  },

  // Events without a picture: the frog, sized to fit, on a soft panel.
  cardBannerPlaceholder: {
    width: '100%',
    height: '140px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.35)',
  },

  cardBannerLogo: {
    height: '62%',
    width: 'auto',
    opacity: 0.85,
  },

  cardBody: {
    padding: '8px 10px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 4,
    textAlign: 'center',
  },

  cardTitle: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: 700,
    color: '#000',
    lineHeight: 1.2,
    minHeight: '1.2em',
    // The card body centres its children, so cap the width or long titles
    // overflow both edges instead of ending in "…".
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  cardLine: {
    fontSize: '0.85rem',
    lineHeight: 1.2,
    minHeight: '1.2em',
    color: '#000',
    wordBreak: 'break-word',
    width: '100%',
  },
};